import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Button,
  Table,
  Group,
  ActionIcon,
  Badge,
  Text,
  Loader,
  Stack,
  Card,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { getAllProductsAdmin, deleteProduct } from '@/lib/admin/products';
import { Product } from '@/types';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAuth } from '@/context/AuthContext';

export default function AdminProductsPage() {
  const navigate = useNavigate();
  const { user, isAdmin: isAdminUser, loading: authLoading } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || !isAdminUser)) {
      navigate('/xyz/login');
      return;
    }

    if (user && isAdminUser) {
      loadProducts();
    }
  }, [user, isAdminUser, authLoading, navigate]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await getAllProductsAdmin();
      setProducts(data);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to load products',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId: string, productName: string) => {
    if (!confirm(`Are you sure you want to delete "${productName}"?`)) {
      return;
    }

    try {
      await deleteProduct(productId);
      notifications.show({
        title: 'Success',
        message: 'Product deleted successfully',
        color: 'green',
      });
      loadProducts();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete product',
        color: 'red',
      });
    }
  };

  if (authLoading || (loading && products.length === 0)) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
        </Container>
      </AdminLayout>
    );
  }

  if (!user || !isAdminUser) {
    return null;
  }

  return (
    <AdminLayout>
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={1}>Products</Title>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/xyz/products/new')}
            >
              Add New Product
            </Button>
          </Group>

          {products.length === 0 ? (
            <Card withBorder padding="xl" radius="md" style={{ textAlign: 'center' }}>
              <Text c="dimmed">No products found. Create your first product!</Text>
            </Card>
          ) : (
            <Card withBorder padding="md" radius="md">
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Price</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Stock</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {products.map((product) => (
                    <Table.Tr key={product.id}>
                      <Table.Td>
                        <Text fw={500}>{product.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{product.price.toFixed(2)} PLN</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{product.category || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{product.stockQuantity ?? '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={product.inStock ? 'green' : 'red'}>
                          {product.inStock ? 'In Stock' : 'Out of Stock'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() => navigate(`/xyz/products/${product.id}`)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDelete(product.id, product.name)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          )}
        </Stack>
      </Container>
    </AdminLayout>
  );
}
